import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Button, Card, List, Flex, Collapse, ConfigProvider, message } from "antd"

import svSE from "antd/lib/locale/sv_SE"
import { useUser } from "../../contexts/UserContext"

import CardProgram from "./CardProgram"

import { fetchAllPrograms, runProgram, abortProgram, stepProgram, fetchProgramState } from "../../api/Cards/programs"
import CreateOrUpdateProgram from "./Modals/createOrUpdateProgram"
import ScheduleModal from "./Modals/schedule"
import { buildmetaData } from "../../utils/helpers"
import { writeProgram, deleteProgram } from "../../api/Cards"
import "./CardPrograms.css"

import { ArrowLeftOutlined, SettingOutlined, PlusOutlined } from "@ant-design/icons"

const CardPrograms = ({ card, programState }) => {
    const { t } = useTranslation()
    
    const { isReadOnly, getToken } = useUser()
    const [messageApi, contextHolder] = message.useMessage()
    
    const [meta, setMeta] = useState()
    const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false)
    const [isProgramModalVisible, setIsProgramModalVisible] = useState(false)
    const [activeProgram, setActiveProgram] = useState(null)

    const [programs, setPrograms] = useState([])
    const [programPosition, setProgramPosition] = useState(-1);
    const [programTime, setProgramTime] = useState(-1)

    const token = useMemo(() => getToken(), [getToken])

    const { status, id, cardIndex } = card

    const fetchData = useCallback(async () => {
        if (!token || status === 3) return
        const data = await fetchAllPrograms(buildmetaData(token, id, cardIndex))
        setPrograms(data)
    }, [token, status, id, cardIndex])

    useEffect(() => {
        fetchData()
    }, [cardIndex, fetchData, id, status, token])

    useEffect(
        () =>
            setMeta({
                token,
                cardIndex: card.cardIndex,
                cardID: card.id
            }),
        [card.cardIndex, card.id, token]
    )

    const updateSucces = (msg) => {
        messageApi.success(msg)
    }

    const updateFail = () => {
        messageApi.error("FAIL")
    }

    

    const showScheduleModal = () => setIsScheduleModalVisible(true)
    const closeScheduleModal = () => setIsScheduleModalVisible(false)

    const showProgramModal = () => setIsProgramModalVisible(true)
    const hideProgramModal = () => setIsProgramModalVisible(false)

    const handleNewProgram = (program) => {
        setActiveProgram(undefined)
        showProgramModal()
    }

    const handleEditSchedule = (program) => {
        setActiveProgram(program)
        showScheduleModal()
    }

    const handleEditProgram = (program) => {
        setActiveProgram(program)
        showProgramModal()
    }
    const handleDeleteProgram = async (program) => {
        //console.log(program);
        const result = await deleteProgram(meta, program)

        const msg = t("toast.message.succes.program.deleted")
        if (result) {
            fetchData()
            updateSucces(msg)
        } else updateFail()
    }

    const handleWriteProgram = async (program) => {
        const currentProgram = {
            programName: program.programName,
            positions: program.positions,
            interval: program.interval,
            programID: program.id,
            hourOfDay: program.hourOfDay
        }

        const result = await writeProgram(meta, currentProgram)
        const msg = t("toast.message.succes.program.changes-saved")
        if (result) {
            fetchData()
            updateSucces(msg)
        } else updateFail()
    }

    const handleProgramSave = (program) => {
        handleWriteProgram(program)
        hideProgramModal()
    }

    const handleRunProgram = async (program) => {
        const result = await runProgram(meta, program)

        const msg = t("toast.message.succes.program.start-run-succesful")
        if (result) {
            fetchData()
            updateSucces(msg)
        } else updateFail()
    }

    
    const handleAbortProgram = async () => {
        const result = await abortProgram(meta)

        const msg = t("toast.message.succes.program.abort")
        if (result) {
            fetchData()
            updateSucces(msg)
        } else updateFail()
    }

    const handleProgramStep = async () => {
        const result = await stepProgram(meta)

        //const msg = t("toast.message.success.program.step")

        if (result) {
            fetchData()
            //updateSucces(msg)
        } else updateFail()

    }

    useEffect(() => {
        //console.log(programState, "program update ");

        // -1 indicates that no program is currently running
        if (programState.programIndex != -1) {
            setProgramPosition(programState.currentPosition)
            setProgramTime(programState.remainingSeconds)
            if (activeProgram == null)
                setActiveProgram(programs[programState.programIndex])
        }
        else { //Program aborted
            if (programPosition != 0)
                setProgramPosition(0)
            if(programTime != 0)
                setProgramTime(0)
            //setActiveProgram(undefined)
        }
    }, [programState])

    const collapseItems = [
        {
            key: "1",
            label: t("page.card-details.program-list.title"),
            children: (
                <List
                    bordered
                    dataSource={programs}
                    renderItem={(program, id) => (
                        <CardProgram
                            key={id}
                            program={{ ...program, id }}
                            isSelected={activeProgram && activeProgram.id === id}
                            onClick={(program) => setActiveProgram(program)}
                            onRunProgram={handleRunProgram}
                            onAbortProgram={handleAbortProgram}
                            onEditSchedule={handleEditSchedule}
                            onEditProgram={handleEditProgram}
                            onDeleteProgram={handleDeleteProgram}
                            onStepProgram={handleProgramStep}
                            currentStep={programPosition}
                            currentTime={programTime}
                            isRunning={programState.programIndex == id}
                        />
                    )}
                />
            )
        }
    ]

    return (
        <ConfigProvider locale={svSE}>
            {contextHolder}
            <div className="pd-details">
                <Card className="pd-details-card">
                    {!isReadOnly && (
                        <Flex gap="small" vertical>
                            
                            <Button icon={<PlusOutlined />} onClick={handleNewProgram} style={{ float: "right", marginLeft: "auto", width: "200px" }}>
                                {t("page.card-details.programs.new-program")}
                            </Button>
                        </Flex>
                    )}
                    {<Collapse items={collapseItems} defaultActiveKey={["1"]} style={{ width: "100%", margin: "1rem 0" }} />}
                </Card>
                {isScheduleModalVisible && (
                    <ScheduleModal program={activeProgram} onSave={handleWriteProgram} onClose={closeScheduleModal} />
                )}
                {!isReadOnly && isProgramModalVisible && (
                    <CreateOrUpdateProgram program={activeProgram} onSave={handleProgramSave} onCancel={hideProgramModal} />
                )}
                
            </div>
        </ConfigProvider>
    )
}

export default CardPrograms
